import {
    SRGBColorSpace,
    MeshBasicMaterial,
    Mesh,
    PlaneGeometry,
    Group,
    RepeatWrapping,
    MultiplyBlending,
    ShaderMaterial,
    Color,
    LinearFilter
} from "three";
import VaultHero from './VaultHero.js'
import gsap from "gsap";
import BackgroundShapes from "./BackgroundShapes";
import BackgroundStars from "./BackgroundStars";

export default class Background {
    constructor(_options) {
        this.experience = new VaultHero()
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.resources = this.experience.resources

        this.setTexture()
        this.setGeometry()
        this.setMaterial()
        this.setBackgroundShapes()
        this.setStars()
        this.setWaves()
        this.setNoise()
        this.setTanning()
        this.appearAnimation()
    }

    setBackgroundShapes() {
        this.backgroundShapes = new BackgroundShapes();
    }

    setTexture() {
        this.noiseTexture = this.resources.items.backgroundNoise;
        this.noiseTexture.colorSpace = SRGBColorSpace;
        this.noiseTexture.generateMipmaps = false
        this.noiseTexture.minFilter = LinearFilter
        this.noiseTexture.magFilter = LinearFilter

        this.tanningTexture = this.resources.items.backgroundTanning;
        this.tanningTexture.colorSpace = SRGBColorSpace;
        this.tanningTexture.generateMipmaps = false
        this.tanningTexture.minFilter = LinearFilter
        this.tanningTexture.magFilter = LinearFilter

        this.tanningTextureMobile = this.resources.items.backgroundTanningMobile
        this.tanningTextureMobile.colorSpace = SRGBColorSpace;
        this.tanningTextureMobile.generateMipmaps = false
        this.tanningTextureMobile.minFilter = LinearFilter
        this.tanningTextureMobile.magFilter = LinearFilter
    }

    setTanning() {
        this.tanningMaterial = new MeshBasicMaterial({
            map: this.sizes.breakpoints.isMobile ? this.tanningTextureMobile : this.tanningTexture,
            transparent: true
        })
        this.tanning = new Mesh(
            this.geometry,
            this.tanningMaterial,
        )
        this.tanning.position.z = 0.04;

        this.sizes.fitToScene(this.tanning)

        this.scene.add(this.tanning);
    }

    setGeometry() {
        this.geometry = new PlaneGeometry(this.sizes.originalAspectRatio * 2, 2, 1, 1);
    }

    setMaterial() {
        const vertexShader = `
            varying vec2 vUv;
            
            void main() {
              vUv = uv;
              gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            }
        `

        const fragmentShader = `
        #define PI 3.14159265359;
uniform float u_amplitude; // Amplitude of the sine wave
uniform float u_frequency; // Frequency of the sine wave
uniform float u_lineWeight; // Thickness of the line
uniform vec2 u_resolution; // Resolution of the screen
uniform float u_wavesSize; // Size of the waves in pixels
uniform float u_time;

varying vec2 vUv;

float plot(vec2 uv, float pct, float lineWeight){
  return smoothstep(pct - lineWeight, pct, uv.x) - smoothstep(pct, pct + lineWeight, uv.x);
}

float wave(vec2 uv, float amplitude, float frequency, float step, float lineWeight){
    return plot(vec2(uv.x - step, uv.y - 0.5), cos(uv.y * frequency - u_time) * amplitude, lineWeight);
}

void main() {
    vec3 colorStart = vec3(1.0, 0.95, 0.3);
    vec3 colorMiddle = vec3(1.0, 1.0, 0.25);
    vec3 color = colorStart;
    float alpha = 0.0;
    
    int waves = int(u_resolution.x / u_wavesSize);
    float normalizedWaveSize = u_wavesSize / u_resolution.x;
    float normalizedAmplitude = u_amplitude / u_resolution.x;
    float normalizedFrequency = u_frequency / u_resolution.y;
    float normalizedLineWeight = u_lineWeight / u_resolution.x;

    float gradientFactor = abs(vUv.x - .5);
    vec3 gradientColor = mix(colorStart, colorMiddle, gradientFactor);

    for (int i = 0; i <= waves + 1; i++) {
        float pct = wave(vUv, normalizedAmplitude, normalizedFrequency, float(i) * normalizedWaveSize, normalizedLineWeight);
        alpha = max(alpha, pct);
        color += pct * gradientColor; 
    }

    gl_FragColor = vec4(color, alpha);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
}
        `

        this.wavesMaterial = new ShaderMaterial(
            {
                uniforms: {
                    u_amplitude: { value: 14 }, // 0.01
                    u_frequency: { value: 6000 }, // 12
                    u_lineWeight: { value: 2 },
                    u_resolution: { value: { x: this.sizes.width, y: this.sizes.height } },
                    u_wavesSize: { value: 24 },
                    u_time: { value: 0 }
                },
                vertexShader: vertexShader,
                fragmentShader: fragmentShader,
                transparent: true
            }
        )

        this.noiseMaterial = new MeshBasicMaterial({
            map: this.noiseTexture,
            blending: MultiplyBlending,
            transparent: true
        });
    }

    setGradient() {
        this.meshGradient = new Mesh(this.geometry, this.materialGradient);

        window.meshGradient = this.meshGradient
        this.sizes.fitToScene(this.meshGradient)

        this.scene.add(this.meshGradient);
    }

    setWaves() {
        const planeWidth = this.sizes.width * this.sizes.em;
        const planeHeight = this.sizes.height * this.sizes.em;

        this.wavesGeometry = new PlaneGeometry(planeWidth, planeHeight, 1, 1);
        this.meshWaves = new Mesh(this.wavesGeometry, this.wavesMaterial);
        this.meshWaves.position.z = 0.01;

        this.scene.add(this.meshWaves);
    }

    setNoise() {
        this.meshNoise = new Mesh(this.geometry, this.noiseMaterial);
        this.meshNoise.position.z = 0.02;

        this.sizes.fitToScene(this.meshNoise)

        this.scene.add(this.meshNoise);
    }

    setStars() {
        this.backgroundStars = new BackgroundStars();
    }

    appearAnimation() {
        const timeline = gsap.timeline();

        timeline.from(this.meshWaves.material, {
            opacity: 0,
            duration: 4,
            ease: "power3.out",
        }, 0)

        timeline.from(this.meshNoise.material, {
            opacity: 0,
            duration: 2,
            ease: "power3.out",
        }, 0)

    }

    resizeWaves() {
        if (this.meshWaves) {
            const planeWidth = this.sizes.width * this.sizes.em;
            const planeHeight = this.sizes.height * this.sizes.em;

            this.meshWaves.geometry.dispose();
            this.meshWaves.geometry = new PlaneGeometry(planeWidth, planeHeight, 1, 1);

            this.wavesMaterial.uniforms.u_resolution.value.x = this.sizes.width;
            this.wavesMaterial.uniforms.u_resolution.value.y = this.sizes.height;
        }
    }

    update() {
        if (this.meshWaves) {
            this.meshWaves.material.uniforms.u_time.value = performance.now() / 1000;
        }
    }

    resize() {
        this.sizes.fitToScene(this.meshNoise)
        this.sizes.fitToScene(this.tanning)
        this.resizeWaves()
        this.backgroundStars.resize()
        this.backgroundShapes.resize()
    }

    breakpointChange() {
        this.tanningMaterial.map = this.sizes.breakpoints.isMobile ? this.tanningTextureMobile : this.tanningTexture;
    }
}
